/**
 * E2E test-mode detection and native-plugin stub knobs.
 *
 * The Android-only Capacitor plugins (RD-service fingerprint capture, biometric
 * auth) cannot run in a browser. When the app runs as a plain web app under the
 * agentic E2E harness we stub them so the full send-money flow is driveable by
 * Playwright. This is STRICTLY dev/test only - never active in a real Capacitor
 * build (those report a non-'web' platform) and only when explicitly enabled.
 *
 * Enable by either:
 *   - building/serving with NEXT_PUBLIC_E2E_MODE=1, or
 *   - opening the app once with `?e2e=1` (persists to sessionStorage), or
 *   - the harness running `sessionStorage.setItem('artham.e2e','1')`.
 *
 * Failure injection (so the agent can drive sad paths) via sessionStorage:
 *   - artham.e2e.rd        = 'fail'  → captureBiometric() throws
 *   - artham.e2e.biometric = 'fail'  → requireBiometric() returns a cancel
 */

const E2E_KEY = 'artham.e2e';

function ss(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** True when the app is running under the E2E harness in a browser. */
export function isE2eMode(): boolean {
  if (import.meta.env.VITE_E2E_MODE === '1') return true;
  const store = ss();
  if (!store) return false;
  if (store.getItem(E2E_KEY) === '1') return true;
  // First-visit opt-in via query param - persist so SPA navigations keep it.
  const params = new URLSearchParams(window.location.search);
  if (params.get('e2e') === '1') {
    store.setItem(E2E_KEY, '1');
    return true;
  }
  return false;
}

function knob(name: string): string | null {
  return ss()?.getItem(`${E2E_KEY}.${name}`) ?? null;
}

/** When true, the stubbed RD-service capture throws (drives scanner-failure UX). */
export function e2eRdShouldFail(): boolean {
  return knob('rd') === 'fail';
}

/** When true, the stubbed biometric gate returns a cancel (drives gate-decline UX). */
export function e2eBiometricShouldFail(): boolean {
  return knob('biometric') === 'fail';
}

/**
 * When '1', the send-money biometric step takes the iOS Face-auth (FaceRD) path
 * instead of the Android fingerprint path, so the FaceRD session→callback→poll
 * flow is driveable in a browser. Set via `artham.e2e.facerd = '1'`.
 */
export function e2eUseFaceRd(): boolean {
  return knob('facerd') === '1';
}

/**
 * Test breadcrumb. Emits a single recognisable console line the E2E agent
 * reads via Playwright's console capture. Cheap and harmless in normal use -
 * a `[e2e]` prefixed line is easy to grep and ignore.
 */
export function tlog(event: string, data?: Record<string, unknown>): void {
  try {
    // eslint-disable-next-line no-console
    console.log(`[e2e] ${JSON.stringify({ event, ts: new Date().toISOString(), ...data })}`);
  } catch {
    /* never let logging break the UI */
  }
}

/**
 * Canned encrypted-PID payload. Opaque to the backend's happy-path handling;
 * shaped like a PidData envelope so it survives any structural sniffing.
 */
export const E2E_FAKE_PID_XML =
  '<?xml version="1.0"?><PidData><Resp errCode="0" fCount="1" fType="2" ' +
  'nmPoints="30" qScore="80"/><DeviceInfo dpId="E2E.MOCK" rdsId="E2E.MOCK.001" ' +
  'rdsVer="1.0.0" mi="E2E" mc="E2EMOCKCERT"/><Skey ci="20250101">E2E-MOCK-SKEY</Skey>' +
  '<Hmac>E2E-MOCK-HMAC</Hmac><Data type="X">E2E-MOCK-ENCRYPTED-PID</Data></PidData>';
